import {
  Menu,
  useCaptionsOptions,
  usePlaybackRateOptions,
} from "@videojs/react";
import { useTranslation } from "react-i18next";
import { ChevronRight, ChevronLeft, Check } from "untitledui-js/react";

const iconClassName = "size-3";
const menuPanelClassName = "absolute inset-0 overflow-auto outline-none z-10";
const availablePlaybackRates = new Set([0.5, 1, 1.5, 2]);

interface MenuOption {
  value: string;
  label: string;
  disabled: boolean;
}

interface SettingsMenuProps {
  type: "captions" | "playback-rate";
  label: string;
  value: string;
  options: MenuOption[];
  onValueChange: (value: string) => void;
}

function SettingsMenu({
  type,
  label,
  value,
  options,
  onValueChange,
}: Readonly<SettingsMenuProps>) {
  return (
    <Menu.Root>
      <Menu.Trigger
        type={type}
        className="flex min-h-8 cursor-pointer items-center justify-between rounded-md px-2.5 aria-expanded:hidden"
        render={(props) => (
          <div {...props}>
            <span>{label}</span>
            <span className="flex items-center justify-center gap-2">
              <Menu.ItemValue />
              <span aria-hidden="true">
                <ChevronRight className={iconClassName} />
              </span>
            </span>
          </div>
        )}
      />
      <Menu.Content className={menuPanelClassName}>
        <Menu.Back className="flex w-full cursor-pointer items-center gap-2 border-b border-secondary-700 px-2.5 dark:border-primary-500">
          <span aria-hidden="true">
            <ChevronLeft className={iconClassName} />
          </span>
          {label}
        </Menu.Back>
        <Menu.RadioGroup
          className="px-2.5"
          value={value}
          onValueChange={onValueChange}
          aria-label={label}
        >
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <Menu.RadioItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className="flex cursor-pointer items-center gap-2"
              >
                <Menu.ItemIndicator checked={isSelected}>
                  <Check className={iconClassName} />
                </Menu.ItemIndicator>
                <span className={isSelected ? "" : "ml-5"}>{option.label}</span>
              </Menu.RadioItem>
            );
          })}
        </Menu.RadioGroup>
      </Menu.Content>
    </Menu.Root>
  );
}

export default function VideoMenu() {
  const { t } = useTranslation();
  const captions = useCaptionsOptions();
  const playbackRate = usePlaybackRateOptions();
  const playbackRateOptions =
    playbackRate?.options.filter((option) =>
      availablePlaybackRates.has(option.rate)
    ) ?? [];
  const hasCaptions = captions?.state.availability === "available";
  const hasPlaybackRate = playbackRate?.state.availability === "available";

  return (
    <Menu.Content className="relative h-(--media-menu-height) w-64 overflow-hidden rounded-md bg-tertiary-100/70 py-1 transition-[height] [--media-popover-boundary-offset:1rem] [--media-popover-side-offset:1rem] dark:bg-secondary-700/70 dark:text-white">
      <Menu.View
        className={`${menuPanelClassName} data-[menu-view-state=inactive]:invisible`}
      >
        {hasPlaybackRate ? (
          <SettingsMenu
            type="playback-rate"
            label={t("Video.playbackSpeed")}
            value={playbackRate.value}
            options={playbackRateOptions}
            onValueChange={playbackRate.setValue}
          />
        ) : null}

        {hasCaptions ? (
          <SettingsMenu
            type="captions"
            label={t("Video.captions")}
            value={captions.value}
            options={captions.options}
            onValueChange={captions.setValue}
          />
        ) : null}
      </Menu.View>
    </Menu.Content>
  );
}
